import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import axios from 'axios';
import { UserCog, ArrowLeft, Mail, User, Building, Phone, Save, Loader2 } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const ClientEdit = () => {
  const navigate = useNavigate();
  const { email } = useParams();
  const { token } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    company: '',
    phone: '',
    is_active: true
  });
  const [originalData, setOriginalData] = useState(null);
  const [originalEmail, setOriginalEmail] = useState('');

  useEffect(() => {
    fetchClient();
  }, [email]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const client = response.data;
      const data = {
        email: client.email || '',
        full_name: client.full_name || '',
        company: client.company || '',
        phone: client.phone || '',
        is_active: client.is_active !== false
      };
      setFormData(data);
      setOriginalData(data);
      setOriginalEmail(client.email);
    } catch (error) {
      console.error('Error fetching client:', error);
      setError('Erro ao carregar dados do cliente');
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = () => {
    if (!originalData) return false;
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!hasChanges()) {
      setError('Nenhuma alteração foi feita');
      return;
    }

    // Validate email format
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Email inválido');
      return;
    }

    setSaving(true);

    try {
      const response = await axios.put(`${API}/admin/clients/${encodeURIComponent(originalEmail)}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Cliente atualizado com sucesso!');
      setOriginalData(formData);
      
      // If email changed, redirect to new URL
      const newEmail = response.data.new_email || formData.email;
      if (newEmail !== originalEmail) {
        setOriginalEmail(newEmail);
      }
      
      setTimeout(() => navigate('/admin/clients'), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao atualizar cliente');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-arcano-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/clients')}
          className="p-2 hover:bg-gray-100 rounded-md transition-colors"
        >
          <ArrowLeft size={24} className="text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <UserCog className="text-arcano-blue" />
            Editar Cliente
          </h1>
          <p className="text-gray-600">{email}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-8 max-w-2xl">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md mb-6">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value.toLowerCase().trim() })}
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                placeholder="email@exemplo.com"
                data-testid="client-email-input"
              />
            </div>
            {formData.email !== originalEmail && formData.email && (
              <p className="mt-1 text-sm text-amber-600">
                Alterar o email irá atualizar todos os contratos e documentos vinculados
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nome Completo *</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                placeholder="Nome do cliente"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Empresa</label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                placeholder="Nome da empresa (opcional)"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Telefone</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                placeholder="(00) 0 0000-0000"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-md border border-gray-200">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-5 h-5 text-arcano-blue rounded focus:ring-arcano-blue"
            />
            <div>
              <label htmlFor="is_active" className="font-medium text-gray-900 cursor-pointer">
                Cliente Ativo
              </label>
              <p className="text-sm text-gray-600">
                Clientes inativos não podem fazer login no sistema
              </p>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/admin/clients')}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !hasChanges()}
              className="flex-1 flex items-center justify-center gap-2 bg-arcano-blue text-white px-6 py-3 rounded-md hover:bg-arcano-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {saving ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save size={20} />
                  Salvar Alterações
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientEdit;
